import type { BookSummary } from '../models';

// IndexedDB database `plume-library` (separate from the `plume` database the
// player and downloads use): one row per cached book (store `books`) and, since
// v2, one chapter list per book for search (store `chapters`), both keyed by
// scope + id. The scope is `abs|<serverUrl>|<userId>|<libraryId>` (data/scope.ts).

const DB_NAME = 'plume-library';
const DB_VERSION = 2;
const BOOKS = 'books';
const CHAPTERS = 'chapters';

export interface CachedBookRow {
  /** `<scope>\u0000<bookId>`. */
  key: string;
  scope: string;
  id: string;
  book: BookSummary;
}

/** A chapter as the search index keeps it. */
export interface IndexedChapter {
  title: string;
  /** Seconds. */
  start: number;
}

/** A book's chapters, read from its full item. */
export interface ChapterRecord {
  bookId: string;
  /** The cached book's `updatedAt` when the chapters were read; a different value means they are stale. */
  updatedAt: number | null;
  /** In playback order, untitled chapters left out. */
  chapters: IndexedChapter[];
}

interface ChapterRow {
  /** `<scope>\u0000<bookId>`. */
  key: string;
  scope: string;
  id: string;
  record: ChapterRecord;
}

let dbPromise: Promise<IDBDatabase> | null = null;

function openLibraryDb(): Promise<IDBDatabase> {
  if (dbPromise) return dbPromise;
  dbPromise = new Promise((resolve, reject) => {
    const req = indexedDB.open(DB_NAME, DB_VERSION);
    req.onupgradeneeded = () => {
      const db = req.result;
      for (const name of [BOOKS, CHAPTERS]) {
        if (db.objectStoreNames.contains(name)) continue;
        const store = db.createObjectStore(name, { keyPath: 'key' });
        store.createIndex('scope', 'scope', { unique: false });
      }
    };
    req.onsuccess = () => {
      const db = req.result;
      db.onversionchange = () => {
        db.close();
        dbPromise = null;
      };
      resolve(db);
    };
    req.onerror = () => {
      dbPromise = null;
      reject(req.error);
    };
  });
  return dbPromise;
}

export const rowKey = (scope: string, id: string): string => `${scope}\u0000${id}`;

function done(tx: IDBTransaction): Promise<void> {
  return new Promise((resolve, reject) => {
    tx.oncomplete = () => resolve();
    tx.onerror = () => reject(tx.error);
    tx.onabort = () => reject(tx.error);
  });
}

/** Every cached book of a scope. */
export async function readScopeRows(scope: string): Promise<BookSummary[]> {
  const db = await openLibraryDb();
  return new Promise((resolve, reject) => {
    const req = db.transaction(BOOKS, 'readonly').objectStore(BOOKS).index('scope').getAll(IDBKeyRange.only(scope));
    req.onsuccess = () => resolve((req.result as CachedBookRow[]).map((r) => r.book));
    req.onerror = () => reject(req.error);
  });
}

/** Inserts or replaces books in a scope. */
export async function putScopeRows(scope: string, books: readonly BookSummary[]): Promise<void> {
  if (books.length === 0) return;
  const db = await openLibraryDb();
  const tx = db.transaction(BOOKS, 'readwrite');
  const store = tx.objectStore(BOOKS);
  for (const book of books) store.put({ key: rowKey(scope, book.id), scope, id: book.id, book } satisfies CachedBookRow);
  await done(tx);
}

/** Replaces a scope's rows in one transaction (full sync prunes deleted items). */
export async function replaceScopeRows(scope: string, books: readonly BookSummary[]): Promise<void> {
  const db = await openLibraryDb();
  const tx = db.transaction(BOOKS, 'readwrite');
  const store = tx.objectStore(BOOKS);
  const keep = new Set(books.map((b) => rowKey(scope, b.id)));
  const cursorReq = store.index('scope').openKeyCursor(IDBKeyRange.only(scope));
  cursorReq.onsuccess = () => {
    const cursor = cursorReq.result;
    if (!cursor) return;
    if (!keep.has(String(cursor.primaryKey))) store.delete(cursor.primaryKey);
    cursor.continue();
  };
  for (const book of books) store.put({ key: rowKey(scope, book.id), scope, id: book.id, book } satisfies CachedBookRow);
  await done(tx);
}

/** Deletes every cached book and chapter list (all scopes). */
export async function clearAllRows(): Promise<void> {
  const db = await openLibraryDb();
  const tx = db.transaction([BOOKS, CHAPTERS], 'readwrite');
  tx.objectStore(BOOKS).clear();
  tx.objectStore(CHAPTERS).clear();
  await done(tx);
}

/** Every chapter record of a scope. */
export async function readScopeChapters(scope: string): Promise<ChapterRecord[]> {
  const db = await openLibraryDb();
  return new Promise((resolve, reject) => {
    const req = db.transaction(CHAPTERS, 'readonly').objectStore(CHAPTERS).index('scope').getAll(IDBKeyRange.only(scope));
    req.onsuccess = () => resolve((req.result as ChapterRow[]).map((r) => r.record));
    req.onerror = () => reject(req.error);
  });
}

/** Inserts or replaces chapter records in a scope. */
export async function putScopeChapters(scope: string, records: readonly ChapterRecord[]): Promise<void> {
  if (records.length === 0) return;
  const db = await openLibraryDb();
  const tx = db.transaction(CHAPTERS, 'readwrite');
  const store = tx.objectStore(CHAPTERS);
  for (const record of records) store.put({ key: rowKey(scope, record.bookId), scope, id: record.bookId, record } satisfies ChapterRow);
  await done(tx);
}

/** Removes the chapter records of books that left a scope. */
export async function deleteScopeChapters(scope: string, bookIds: readonly string[]): Promise<void> {
  if (bookIds.length === 0) return;
  const db = await openLibraryDb();
  const tx = db.transaction(CHAPTERS, 'readwrite');
  const store = tx.objectStore(CHAPTERS);
  for (const id of bookIds) store.delete(rowKey(scope, id));
  await done(tx);
}
